/**
 * The per-person notification grid's display vocabulary — each alert type's
 * label and what it covers, the channels, and which types each channel can
 * carry.
 *
 * Shared by the two pages that name alert types to an operator: `/config`
 * (the grid a person ticks) and `/email-templates` (which alert types can
 * arrive by email, and who gets them). One copy, so the two pages cannot
 * describe the same alert differently.
 */

// Migration 192 — the per-person notification grid's display vocabulary.
// Keep the three consts in LOCKSTEP with `STAFF_NOTIFICATION_GRID_TYPES` /
// `STAFF_NOTIFICATION_CHANNEL_CAPABILITIES` in
// packages/canonical-data/src/l2-yaml.ts — the action's JSON Schema rejects
// any tick outside the capability map, so drift here surfaces as a 400 on
// save, never a silent success. Tuples: [type, label, hint].
export const NOTIFICATION_GRID_TYPES = [
  [
    "payments",
    "Payment alerts",
    "Payment received / failed / refunded FYIs, incl. membership-payment issues and dunning.",
  ],
  [
    "bookings",
    "Booking alerts",
    "Booking created / moved / cancelled FYIs. You aren't told about changes you make yourself.",
  ],
  [
    "running_late",
    "Running late (your sessions)",
    "A member tells the assistant they are running late for a session you train today — only that session's trainer is told, nobody else. On for everyone with a phone. If the trainer has this off or can't be reached, the “Talk to a human” rail is told instead, so the message still gets passed on.",
  ],
  [
    "members",
    "New-member alerts",
    "New member / lead FYIs. You aren't told about a member you add yourself.",
  ],
  [
    "handoff",
    "“Talk to a human” escalations",
    "Paged when a member asks to speak to a human — including the '💬 Talk to a human' button in chat. If nobody has this ticked anywhere, unmuted admins are paged so a request never strands.",
  ],
  [
    "unanswered",
    "Unanswered-message alerts",
    "Paged when a message arrives on a channel the assistant isn't answering — Instagram/email until the member's AI pill is set, WhatsApp when the studio's AI is off. WhatsApp: one alert per message. Instagram and email: at most one alert per conversation per clock half-hour, quoting the first message. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "home_session",
    "At-home session requests",
    "The chat agent never books home sessions — it escalates them. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "enquiries",
    "Website contact-form enquiries",
    "Paged when someone sends the website contact form, with what they wrote. Fires whether or not they are already a member — unlike New-member alerts, which announce the row and stay silent for an existing member. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "ai_replied",
    "AI-replied alerts",
    "Paged when the assistant answers someone by itself on email or Instagram \u2014 the channels where it only replies to a member whose AI pill you switched on by hand. The mirror of Unanswered-message alerts, and the half that used to be silent. One alert per message. Same nobody-ticked \u2192 unmuted-admins fallback as handoff.",
  ],
  [
    "ai_off_reply",
    "Replies while the AI is off (admin-only)",
    "Paged when a member whose WhatsApp AI you switched off on their profile writes in, so you see their reply without reopening the profile. Quotes the message, names the member and links to their profile. One alert per message, a reaction such as 👍 included; a voice note or photo is announced, not transcribed. Admin-only lane \u2014 for a non-admin the tick is inert. Nobody ticked means nobody gets it.",
  ],
  [
    "unsubscribes",
    "Unsubscribe requests",
    "Paged when a member emails asking to stop marketing. Clavis opts them out of all marketing by itself (booking emails and reminders still go) and tells you, quoting the email and linking to their profile. An email that only might be a request is sent to you to check, and nothing is changed. Nobody ticked means nobody gets it — the opt-out happens either way.",
  ],
  [
    "instagram",
    "Instagram alerts",
    "DM notifier + comment/mention router — and, when the funnel's approval WhatsApp is switched on below, the tappable Approve/Reject card for every staged funnel DM. WhatsApp is the channel that rail uses, so a person without the WhatsApp tick gets no card.",
  ],
  [
    "instagram_digest",
    "Daily Instagram summary",
    "One message at 07:00 summing up yesterday's Instagram DM chats with members and leads: who wrote, what each chat was about, and which are still waiting on a reply (waiting ones first). PR contacts are left out. A quiet day still gets a one-line message. Separate from Instagram alerts above — tick either or both. Nobody ticked means nobody gets it.",
  ],
  ["rota", "Rota alerts", "Coverage-watchdog gap alerts."],
  [
    "ratings",
    "Session-rating alerts",
    "Fires on EVERY rating, comment included — not just bad ones.",
  ],
  [
    "digests",
    "Daily digests (admin-only)",
    "Churn / renewal / chat-config nudge / chat-efficiency / tasks morning. These lanes page admins only — for a non-admin the tick is inert.",
  ],
  [
    "infra",
    "Infrastructure alerts (admin-only)",
    "DLQ / idempotency / integration health / inbox alarm / watchdog notify legs. Admin-only lanes — for a non-admin the tick is inert.",
  ],
  [
    "ads_summary",
    "Daily ads summary (admin-only)",
    "The daily ads check's morning message, after its 10:00 run: a one-line verdict, then the key numbers for yesterday and the last 7 days from Google Ads, Meta, Google Analytics, Clavis payments, Search Console, Clarity, page speed and the Business Profile. Admin-only lane — for a non-admin the tick is inert. Nobody ticked means nobody gets it.",
  ],
  [
    "ads_alerts",
    "Ads alerts (admin-only)",
    "A real problem the daily ads check found (the site or a landing page down, tracking broken, spend over the daily cap or in an unexpected campaign, a disapproved ad, the Meta token close to lapsing), or the check itself failing to finish. Admin-only lane — for a non-admin the tick is inert. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "chat_review",
    "Daily chat review (admin-only)",
    "Each morning, a review of yesterday's chats with members and staff: how many, what went wrong (worst first, already fixed or not), and questions the knowledge base could not answer. Admin-only lane — for a non-admin the tick is inert. Nobody ticked means nobody gets it.",
  ],
  [
    "newsletter",
    "Newsletter learnings (admin-only)",
    "What each email newsletter showed 48 hours, 7 days and 14 days after sending (the subject test, clicks, unsubscribes) and what to test next, from the newsletter lane. The numbers behind it are on /campaigns. Admin-only lane — for a non-admin the tick is inert. Nobody ticked means nobody gets it.",
  ],
  [
    "sales_briefing",
    "Sales briefing (admin-only)",
    "About 05:00 and 17:00, the sales lane's summary: who to contact today and why (unfinished checkouts, unanswered messages, new prospects, former clients worth a message), what changed since the last run, and member records waiting for a clean-up decision. It sends nothing to members by itself. Admin-only lane — for a non-admin the tick is inert. Nobody ticked means nobody gets it.",
  ],
  [
    "reviews",
    "Monthly review calls (admin-only)",
    "Your reminder on the day of a trainer's review call you confirmed, and an alert when no review-call time is left to offer a trainer, so you arrange it with them directly. The ballot that confirms a picked time arrives on Staff-decision ballots below. Admin-only lane — for a non-admin the tick is inert. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "checkout",
    "Abandoned-checkout alerts",
    "The plain abandoned/expired checkout + failed-payment alerts.",
  ],
  [
    "checkout_leads",
    "Checkout lead cards (approve/reject)",
    "The 'UNKNOWN buyer' card with APPROVE/REJECT buttons that creates a lead + sends the recovery email. First tap wins; later taps see 'Already handled.'",
  ],
  [
    "checkout_digest",
    "Daily checkout-funnel digest",
    "The daily 07:30 purchase-funnel digest.",
  ],
  [
    "error",
    "Error notifications",
    "The 15-min error-alert digest (audit_error_alerter). WhatsApp-only by capability.",
  ],
  [
    "decisions",
    "Staff-decision ballots",
    "Where your ballots arrive. 'Takes part in staff decisions' below decides WHO votes; this decides WHERE. Ballots bypass the mute — the mute governs FYIs, not decisions.",
  ],
] as const;

export const NOTIFICATION_GRID_CHANNELS = [
  ["whatsapp", "WhatsApp", "needs a phone number on this row"],
  ["telegram", "Telegram", "needs the telegram chat id above"],
  ["email", "Email", "needs the notify email above"],
] as const;

// Which types each channel section lists — the connector's actual reach.
// Telegram has no error lane (WhatsApp-template-only); email carries only
// the event classes (no checkout/error/decisions machinery).
export const NOTIFICATION_GRID_CAPABILITIES: Record<string, ReadonlyArray<string>> = {
  whatsapp: NOTIFICATION_GRID_TYPES.map(([t]) => t),
  telegram: NOTIFICATION_GRID_TYPES.map(([t]) => t).filter(
    (t) => t !== "error",
  ),
  email: [
    "payments",
    "bookings",
    "members",
    "handoff",
    "unanswered",
    "home_session",
    "enquiries",
    "ai_replied",
    "ai_off_reply",
    "unsubscribes",
    "instagram",
    "instagram_digest",
    "rota",
    "ratings",
    "digests",
    "infra",
    "ads_summary",
    "ads_alerts",
    "chat_review",
    "newsletter",
    "sales_briefing",
    "running_late",
    "reviews",
  ],
};

// What a person added with "+ Add staff" on /config starts with: the grid the
// writer seeds for a brand-new phone (`DEFAULT_NOTIFICATION_GRID` in
// packages/canonical-data/src/l2-yaml.ts) — the formerly-default-open lanes
// on WhatsApp, ballots on both tap rails, running-late notices for the
// person's own sessions, everything else off until ticked. The editor submits
// a grid on every save and the writer's submitted grid wins, so THIS copy is
// what a new person actually gets; it is pinned to the writer's by
// apps/dashboard/tests/notification-grid-new-staff.test.ts. (2026-10-18,
// `member-running-late`: until now the copy sat inline in StudioConfig.tsx,
// unchecked, and a class added to the writer's default would have been
// missing for everyone added on /config.)
export const NEW_STAFF_NOTIFICATION_GRID: Readonly<
  Record<string, ReadonlyArray<string>>
> = {
  payments: ["whatsapp"],
  bookings: ["whatsapp"],
  members: ["whatsapp"],
  instagram: ["whatsapp"],
  rota: ["whatsapp"],
  ratings: ["whatsapp"],
  digests: ["whatsapp"],
  infra: ["whatsapp"],
  decisions: ["whatsapp", "telegram"],
  running_late: ["whatsapp"],
};
